import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Form, message, Input, Space, Button, Modal, Table, Spin } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import useILTInstructors from '../../gql/hooks/useILTInstructors';
import useInstructorCreate from '../../gql/hooks/useInstructorCreate';
import useInstructorUpdate from '../../gql/hooks/useInstructorUpdate';
import useInstructorDelete from '../../gql/hooks/useInstructorDelete';
import { validateEmail } from '../../utils';
import './styles.less';

const ILTInstructors = ( { modifiedCallback, ref } ) => {
    const [ form ] = Form.useForm();
    const { instructors, instructorsLoading, instructorsError } = useILTInstructors();
    const [ showAddModal, setShowAddModal ] = useState( false );
    const [ showEditModal, setShowEditModal ] = useState( false );
    const [ modalIsSaving, setModalIsSaving ] = useState( false );
    const [ editId, setEditId ] = useState();
    const { createInstructor } = useInstructorCreate();
    const { saveInstuctor } = useInstructorUpdate();
    const { deleteInstructor } = useInstructorDelete();
    const [ instructorList, setInstuctorList ] = useState( [] );

    useImperativeHandle( ref, () => ( {
    } ) );

    useEffect( () => {
        if ( instructors ) {
            setInstuctorList( instructors );
        }
    }, [ instructors ] );

    /*************************************
     *  ANCHOR Add Instructor
     *************************************/
 
    const handleAddInstructorSubmit = async ( values ) => {
        setModalIsSaving( true );
       
        const input = { 
            name: values.instructorName || '',
            title: values.instructorTitle || '',
            description: values.instructorDescription || '',
            emailAddress: values.instructorEmailAddress || '' 
        };
 
        const response = await createInstructor( input );

        if ( response.success ) { 
            setInstuctorList( [ 
                {   
                    ...response.response.data.createInstructor,
                    key: response.response.data.createInstructor.id 
                },
                ...instructorList 
            ] );
        } else {
            message.error( 'Error creating instuctor. Please try again later.' );
        }
        
        form.resetFields();
        setModalIsSaving( false );
        setShowAddModal( false );
    };

    const handleAddClick = () => {
        setShowAddModal( true );
    };
    
    const handleAddClose = () => {
        form.submit();
    };
    
    const handleAddCancel = () => {
        setShowAddModal( false );
        form.resetFields();
    };
 
    /*************************************
     *  ANCHOR Edit Instructor
     *************************************/
    
    const handleEditInstructorSubmit = async ( values ) => {
        setModalIsSaving( true );
       
        const input = { 
            id: editId,
            name: values.instructorName || '',
            title: values.instructorTitle || '',
            description: values.instructorDescription || '',
            emailAddress: values.instructorEmailAddress || '' 
        };
 
        const response = await saveInstuctor( input );

        if ( response.success ) { 
            const updated = instructorList.map( i => {
                if ( i.id === editId ) {
                    return {
                        ...response.response.data.patchInstructor,
                        key: editId
                    };
                } else {
                    return i;
                }
            } );

            setInstuctorList( updated );
        } else {
            message.error( 'Error creating instuctor. Please try again later.' );
        }
        
        form.resetFields();
        setModalIsSaving( false );
        setShowEditModal( false );
    };
   
    const handleEditClick = ( id ) => {
        const item = instructorList.find( i => i.id === id );
        
        if ( !item ) {
            return;
        }
        
        setEditId( item.id );
        form.setFieldsValue( { instructorName: item.name } );
        form.setFieldsValue( { instructorTitle: item.title } );
        form.setFieldsValue( { instructorDescription: item.description } );
        form.setFieldsValue( { instructorEmailAddress: item.emailAddress } );

        setShowEditModal( true );
    };

    const handleEditClose = () => {
        form.submit();
    };

    const handleEditCancel = () => {
        setShowEditModal( false );
        form.resetFields();
    };

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/
 
    const handleDeleteClick = async ( i ) => {
        const response = await deleteInstructor( i );
        
        if ( response ) {
            setInstuctorList( instructorList.filter( il => il.id !== i ) );
        } else {
            message.error( 'Error deleting instuctor, please try again later.' );
        }
    };

    /*************************************
     *  ANCHOR Table setup
     *************************************/
 
    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sortKey: 'name',
            fixed: 'left',
        },
        {
            dataIndex: 'title',
            key: 'title', 
            title: 'Title',
            sortKey: 'title',
            fixed: 'left',
        },
        {
            dataIndex: 'emailAddress',
            key: 'emailAddress', 
            title: 'Email',
            sortKey: 'emailAddress',
            fixed: 'left',
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => handleEditClick( item.id ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the instructor <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            } 
            
        }
    ];

    const getTableHeader = () => {
        return ( 
            <div className="toolbar">
                <div className="left">
                    { /* <h3>Instructors</h3> */ }
                </div>

                <div className="right">
                    <Button   
                        type="text"
                        icon={ <PlusOutlined /> }
                        onClick= { () => handleAddClick() }
                    >
                        Add Instructor  
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="form-item-instructors">
            
            { instructorsLoading && 
                <Spin />
            }
            
            { instructorsError && 
                <Alert 
                    type="error"
                    message="Error"
                    description="An unknown error occurred. Please try again later."
                />
            }

            { !instructorsLoading && !instructorsError &&
                <>
                    <Table
                        columns={ tableColumns }
                        dataSource={ instructorList } 
                        title={ () => getTableHeader() }
                        scroll={ { x: 'max-content' } } 
                        pagination={ { 
                            defaultPageSize: 5,
                            showSizeChanger: false
                        } }
                    />
                    
                    { showAddModal && 
                        <Modal
                            title="Add Instructor"
                            centered
                            open={ showAddModal }
                            onOk={ handleAddClose }
                            onCancel={ handleAddCancel } 
                            confirmLoading={ modalIsSaving }
                            width={ 800 }
                            okText="Save"
                            wrapClassName="add-instructor-modal"
                            maskClosable={ false }
                        >
                            <Form
                                form={ form }
                                name="add-form"
                                layout="vertical"
                                onFinish={ handleAddInstructorSubmit }
                                scrollToFirstError
                                disabled={ modalIsSaving }
                                initialValues={ 
                                    { 
                                        instructorName: '',
                                        instructorTitle: '',
                                        instructorDescription: '',
                                        instructorEmailAddress: ''
                                    }
                                }
                            >   
                                <Form.Item
                                    name="instructorName"
                                    label="Name"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Name is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorTitle"
                                    label="Title"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorDescription"
                                    label="Description"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorEmailAddress"
                                    label="Email"
                                    rules={ [
                                        {
                                            max: 200,
                                            validateTrigger: [],
                                            message: 'Email must be less than 200 characters long.',
                                        },
                                        {
                                            message: 'Must be a valid email address',
                                            validateTrigger: [],
                                            validator: ( _, value ) => {
                                                if ( !value ) {
                                                    return Promise.resolve();
                                                }
                                    
                                                if ( validateEmail( value ) ) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject();
                                                }
                                            }
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>
                            </Form>
                        </Modal>
                    }

                    { showEditModal && 
                        <Modal
                            title="Edit Instructor"
                            centered
                            open={ showEditModal }
                            onOk={ handleEditClose }
                            onCancel={ handleEditCancel } 
                            confirmLoading={ modalIsSaving }
                            width={ 800 }
                            okText="Save"
                            wrapClassName="edit-instructor-modal"
                            maskClosable={ false }
                        >
                            <Form
                                form={ form }
                                name="edit-form"
                                layout="vertical"
                                onFinish={ handleEditInstructorSubmit }
                                scrollToFirstError
                                disabled={ modalIsSaving }
                                initialValues={ 
                                    { 
                                        instructorName: '',
                                        instructorTitle: '',
                                        instructorDescription: '',
                                        instructorEmailAddress: ''
                                    }
                                }
                            >   
                                <Form.Item
                                    name="instructorName"
                                    label="Name"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Name is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorTitle"
                                    label="Title"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorDescription"
                                    label="Description"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="instructorEmailAddress"
                                    label="Email"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>
                            </Form>
                        </Modal>
                    }
                </>
            }
        </div>
    );
};

export default ILTInstructors;
